import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Sunrise, AlertTriangle, Eye, EyeOff, Loader2 } from 'lucide-react';
import { usePermissions } from '@/components/PermissionsProvider';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CLASSIC_BG_IMAGE = 'https://static.prod-images.emergentagent.com/jobs/b57813e0-5c3f-47ce-8b83-46eb70572e41/images/d589b8d282d757d613c71187065b1d1426789ff79b663ae8439203cc8c671a78.png';

export default function Login({ setUser }) {
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    client_id: (searchParams.get('client_id') || 'FAGIOLI').toUpperCase(),
    email: searchParams.get('email') || '',
    password: '',
  });
  const [branding, setBranding] = useState({ theme: 'classic' });
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [forgotError, setForgotError] = useState('');
  const navigate = useNavigate();
  const { refresh: refreshPermissions } = usePermissions();

  // Fetch per-tenant login branding (debounced on Client ID changes) so the login
  // screen can render a tenant-specific theme. Defaults to the classic login.
  useEffect(() => {
    const cid = formData.client_id.trim().toLowerCase();
    if (!cid) { setBranding({ theme: 'classic' }); return; }
    const t = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/tenants/${encodeURIComponent(cid)}/branding`);
        setBranding(res.data && res.data.theme ? res.data : { theme: 'classic' });
      } catch {
        setBranding({ theme: 'classic' });
      }
    }, 400);
    return () => clearTimeout(t);
  }, [formData.client_id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/auth/login`, {
        client_id: formData.client_id.toLowerCase(),
        email: formData.email.toLowerCase(),
        password: formData.password,
      });
      localStorage.setItem('token', response.data.access_token);
      if (response.data.tenant) {
        localStorage.setItem('tenant', JSON.stringify(response.data.tenant));
      }
      localStorage.setItem('tenant_branding', JSON.stringify(branding || { theme: 'classic' }));
      setUser(response.data.user);
      refreshPermissions();
      toast.success('Login successful');
      navigate('/');
    } catch (err) {
      const msg = err.response?.data?.detail || 'Authentication failed';
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    setForgotError('');
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, {
        client_id: formData.client_id.toLowerCase(),
        email: formData.email.toLowerCase(),
      });
      setForgotSent(true);
    } catch (err) {
      setForgotError(err.response?.data?.detail || 'Could not send reset link. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const openForgot = () => { setError(''); setForgotSent(false); setForgotError(''); setForgotMode(true); };

  if (forgotMode) {
    return (
      <ForgotPasswordCard
        formData={formData}
        setFormData={setFormData}
        onSubmit={handleForgot}
        onBack={() => setForgotMode(false)}
        sent={forgotSent}
        error={forgotError}
        submitting={submitting}
      />
    );
  }

  if (branding.theme === 'industrial-hero') {
    return (
      <IndustrialHeroLogin
        branding={branding}
        formData={formData}
        setFormData={setFormData}
        onSubmit={handleSubmit}
        error={error}
        submitting={submitting}
        onForgot={openForgot}
      />
    );
  }

  return (
    <ClassicLogin
      formData={formData}
      setFormData={setFormData}
      onSubmit={handleSubmit}
      submitting={submitting}
      error={error}
      onForgot={openForgot}
    />
  );
}

/* --------------------------- Forgot password ---------------------------- */
function ForgotPasswordCard({ formData, setFormData, onSubmit, onBack, sent, error, submitting }) {
  return (
    <div className="min-h-screen flex items-center justify-center p-6 relative"
      style={{ backgroundImage: `url(${CLASSIC_BG_IMAGE})`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat' }}>
      <div className="absolute inset-0 bg-slate-900/60" />
      <Card className="w-full max-w-md shadow-2xl border-0 relative z-10 bg-white/95 backdrop-blur-sm" data-testid="forgot-password-card">
        <CardHeader className="space-y-3">
          <div className="flex flex-col items-center justify-center mb-2">
            <div className="bg-gradient-to-r from-amber-500 to-amber-600 p-4 rounded-lg mb-3 shadow-lg">
              <Sunrise className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-2xl font-bold tracking-tight text-slate-900">EQUIPR Horizon</h1>
          </div>
          <CardTitle className="text-xl font-semibold text-center tracking-tight text-slate-700">Reset your password</CardTitle>
        </CardHeader>
        <CardContent>
          {sent ? (
            <div className="text-center space-y-4" data-testid="forgot-sent-message">
              <p className="text-sm text-slate-600">
                If an account with that email exists, we've sent a password reset link. Please check your inbox (and spam folder). The link is valid for 1 hour.
              </p>
              <Button type="button" onClick={onBack} data-testid="forgot-back-button"
                className="w-full bg-amber-500 hover:bg-amber-600 text-white shadow-md rounded-md h-10 font-medium transition-colors">
                Back to Sign In
              </Button>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="space-y-4">
              <p className="text-sm text-slate-500">Enter your Client ID and email and we'll send you a link to reset your password.</p>
              {error && (
                <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-700" data-testid="forgot-error">
                  <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="fp_client_id" className="text-sm font-semibold text-slate-700">Client ID</Label>
                <Input id="fp_client_id" data-testid="forgot-client-id-input" value={formData.client_id}
                  onChange={(e) => setFormData({ ...formData, client_id: e.target.value.toUpperCase() })}
                  required placeholder="Your organization's Client ID" className="h-10 border-slate-300 uppercase" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="fp_email" className="text-sm font-semibold text-slate-700">Email</Label>
                <Input id="fp_email" type="email" data-testid="forgot-email-input" value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required placeholder="your@email.com" className="h-10 border-slate-300" />
              </div>
              <Button type="submit" data-testid="forgot-submit-button" disabled={submitting}
                className="w-full bg-amber-500 hover:bg-amber-600 text-white shadow-md rounded-md h-10 font-medium transition-colors">
                {submitting ? 'Sending…' : 'Send reset link'}
              </Button>
              <button type="button" onClick={onBack} data-testid="forgot-back-link"
                className="w-full text-sm text-slate-500 hover:text-slate-700 mt-1">
                Back to Sign In
              </button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/* ----------------------------- Classic login ----------------------------- */
function ClassicLogin({ formData, setFormData, onSubmit, submitting, error, onForgot }) {
  return (
    <div
      className="min-h-screen flex items-center justify-center p-6 relative"
      style={{
        backgroundImage: `url(${CLASSIC_BG_IMAGE})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
    >
      <div className="absolute inset-0 bg-slate-900/60" />
      <Card className="w-full max-w-md shadow-2xl border-0 relative z-10 bg-white/95 backdrop-blur-sm" data-testid="login-card">
        <CardHeader className="space-y-3">
          <div className="flex flex-col items-center justify-center mb-2">
            <div className="bg-gradient-to-r from-amber-500 to-amber-600 p-4 rounded-lg mb-3 shadow-lg">
              <Sunrise className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-2xl font-bold tracking-tight text-slate-900">EQUIPR Horizon</h1>
          </div>
          <CardTitle className="text-xl font-semibold text-center tracking-tight text-slate-700">Sign In</CardTitle>
          <p className="text-sm text-center text-slate-500">Long-Term Equipment &amp; Resource Planning</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} className="space-y-4">
            {error && (
              <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-700" data-testid="login-error">
                <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="client_id" className="text-sm font-semibold text-slate-700">Client ID</Label>
              <Input id="client_id" data-testid="client-id-input" value={formData.client_id}
                onChange={(e) => setFormData({ ...formData, client_id: e.target.value.toUpperCase() })}
                required placeholder="Enter your organization's Client ID" className="h-10 border-slate-300 uppercase" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-semibold text-slate-700">Email</Label>
              <Input id="email" type="email" data-testid="email-input" value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required placeholder="your@email.com" className="h-10 border-slate-300" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-semibold text-slate-700">Password</Label>
              <Input id="password" type="password" data-testid="password-input" value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })} required className="h-10 border-slate-300" />
            </div>
            <Button type="submit" data-testid="submit-button" disabled={submitting}
              className="w-full bg-amber-500 hover:bg-amber-600 text-white shadow-md rounded-md h-10 font-medium transition-colors">
              {submitting ? 'Signing In…' : 'Sign In'}
            </Button>
            <button type="button" onClick={onForgot} data-testid="forgot-password-link"
              className="w-full text-sm text-amber-600 hover:text-amber-700 font-medium mt-1">
              Forgot password?
            </button>
          </form>
          <p className="mt-4 text-xs text-center text-slate-400">Contact your administrator for account access</p>
        </CardContent>
      </Card>
      <div className="absolute bottom-4 text-center w-full">
        <span className="text-xs text-slate-400/70" data-testid="login-version">EQUIPR Horizon v{process.env.REACT_APP_VERSION || '0.0.0'}</span>
      </div>
    </div>
  );
}

/* ------------------------- Industrial hero login ------------------------- */
function IndustrialHeroLogin({ branding, formData, setFormData, onSubmit, error, submitting, onForgot }) {
  const images = Array.isArray(branding.hero_images) && branding.hero_images.length
    ? branding.hero_images : [CLASSIC_BG_IMAGE];
  const accent = branding.accent || '#DC2626';
  const accentHover = branding.accent_hover || '#EF4444';
  const [active, setActive] = useState(0);
  const [showPw, setShowPw] = useState(false);
  const reduceMotion = useRef(typeof window !== 'undefined' && window.matchMedia
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches);

  useEffect(() => {
    if (images.length < 2 || reduceMotion.current) return;
    const id = setInterval(() => setActive((p) => (p + 1) % images.length), 6000);
    return () => clearInterval(id);
  }, [images.length]);

  // Preload images for smoother cross-fade
  useEffect(() => {
    images.forEach((src) => { const im = new Image(); im.src = src; });
  }, [images]);

  return (
    <div className="relative min-h-screen w-full flex flex-col bg-black overflow-hidden" data-testid="login-hero">
      {/* Rotating hero background */}
      <div className="absolute inset-0 w-full h-full z-0">
        {images.map((src, i) => (
          <div
            key={src}
            className="absolute inset-0 w-full h-full bg-cover bg-center transition-opacity ease-in-out"
            style={{ backgroundImage: `url(${src})`, opacity: i === active ? 1 : 0, transitionDuration: '1500ms' }}
            aria-hidden="true"
          />
        ))}
        <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/55 to-black/85" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/30" />
      </div>

      {/* Light header — logo only (no nav links) */}
      <header className="relative z-50 h-16 md:h-20 px-6 md:px-12 flex items-center bg-white border-b border-zinc-200 shadow-sm">
        <div className="flex items-center gap-2.5" data-testid="login-hero-logo">
          <div className="w-9 h-9 rounded-md flex items-center justify-center" style={{ backgroundColor: accent }}>
            <Sunrise className="w-5 h-5 text-white" />
          </div>
          <span className="font-condensed text-2xl font-extrabold uppercase tracking-tight text-zinc-900 leading-none">
            EQUIPR <span style={{ color: accent }}>Horizon</span>
          </span>
        </div>
      </header>

      {/* Main */}
      <main className="relative z-10 flex-1 w-full max-w-7xl mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center py-10">
        {/* Tagline */}
        <div className="col-span-1 lg:col-span-7 order-2 lg:order-1">
          <div className="anim-slide-right" style={{ animationDelay: '120ms' }}>
            <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-white/70 mb-5">
              <span className="h-px w-8" style={{ backgroundColor: accent }} />
              EQUIPR Horizon Platform
            </span>
            <h1 className="font-condensed font-black uppercase text-white leading-[0.95] tracking-tight text-5xl sm:text-6xl lg:text-7xl max-w-3xl drop-shadow-[0_4px_16px_rgba(0,0,0,0.85)]">
              {branding.tagline || 'Master Data Asset List Control & Master Manpower Data Planning'}
            </h1>
            {branding.subtitle && (
              <p className="mt-6 text-base sm:text-lg text-white/75 max-w-xl leading-relaxed">{branding.subtitle}</p>
            )}
          </div>
        </div>

        {/* Login card */}
        <div className="col-span-1 lg:col-span-5 order-1 lg:order-2 flex flex-col items-center lg:items-end gap-4">
          <form
            onSubmit={onSubmit}
            data-testid="login-card"
            className="anim-slide-left w-full max-w-md backdrop-blur-xl bg-black/70 border border-white/10 rounded-xl p-8 lg:p-10 shadow-[0_8px_40px_rgba(0,0,0,0.55)]"
            style={{ animationDelay: '300ms' }}
          >
            <h2 className="font-condensed text-3xl font-extrabold uppercase tracking-tight text-white mb-1">Login</h2>
            <p className="text-sm text-zinc-400 mb-6">Sign in to your EQUIPR Horizon workspace</p>

            {error && (
              <div className="mb-5 flex items-start gap-2 rounded-md border border-red-500/40 bg-red-500/10 px-3 py-2.5 text-sm text-red-300" data-testid="login-error">
                <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label htmlFor="client_id" className="block text-xs font-semibold uppercase tracking-wider text-zinc-300 mb-1.5">Client ID</label>
                <input id="client_id" data-testid="client-id-input" value={formData.client_id}
                  onChange={(e) => setFormData({ ...formData, client_id: e.target.value.toUpperCase() })}
                  required placeholder="Your organization's Client ID"
                  className="w-full bg-white/5 border border-white/20 rounded-md px-4 py-3 text-white uppercase placeholder:text-zinc-500 placeholder:normal-case focus:outline-none focus:ring-2 focus:border-transparent transition-all"
                  style={{ '--tw-ring-color': `${accent}80` }}
                  onFocus={(e) => (e.target.style.boxShadow = `0 0 0 2px ${accent}80`)}
                  onBlur={(e) => (e.target.style.boxShadow = 'none')} />
              </div>
              <div>
                <label htmlFor="email" className="block text-xs font-semibold uppercase tracking-wider text-zinc-300 mb-1.5">Email</label>
                <input id="email" type="email" data-testid="email-input" value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required placeholder="your@email.com"
                  className="w-full bg-white/5 border border-white/20 rounded-md px-4 py-3 text-white placeholder:text-zinc-500 focus:outline-none transition-all"
                  onFocus={(e) => (e.target.style.boxShadow = `0 0 0 2px ${accent}80`)}
                  onBlur={(e) => (e.target.style.boxShadow = 'none')} />
              </div>
              <div>
                <label htmlFor="password" className="block text-xs font-semibold uppercase tracking-wider text-zinc-300 mb-1.5">Password</label>
                <div className="relative">
                  <input id="password" type={showPw ? 'text' : 'password'} data-testid="password-input" value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required placeholder="••••••••"
                    className="w-full bg-white/5 border border-white/20 rounded-md px-4 py-3 pr-11 text-white placeholder:text-zinc-500 focus:outline-none transition-all"
                    onFocus={(e) => (e.target.style.boxShadow = `0 0 0 2px ${accent}80`)}
                    onBlur={(e) => (e.target.style.boxShadow = 'none')} />
                  <button type="button" onClick={() => setShowPw((s) => !s)} data-testid="toggle-password-visibility"
                    aria-label={showPw ? 'Hide password' : 'Show password'}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-white transition-colors">
                    {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            </div>

            <button type="submit" data-testid="submit-button" disabled={submitting}
              className="mt-7 w-full flex items-center justify-center gap-2 text-white font-bold text-base uppercase tracking-wider py-3.5 rounded-md transition-all duration-200 transform hover:-translate-y-0.5 disabled:opacity-70 disabled:hover:translate-y-0"
              style={{ backgroundColor: accent, boxShadow: `0 0 18px ${accent}40` }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = accentHover; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = accent; }}>
              {submitting ? (<><Loader2 className="w-4 h-4 animate-spin" /> Signing In…</>) : 'Login'}
            </button>
            <button type="button" onClick={onForgot} data-testid="forgot-password-link"
              className="mt-3 w-full text-sm font-medium text-zinc-300 hover:text-white transition-colors">
              Forgot password?
            </button>
          </form>

          {branding.notice && (
            <div className="anim-rise w-full max-w-md backdrop-blur-md bg-black/75 border border-white/10 rounded-lg px-4 py-3 text-center shadow-lg" style={{ animationDelay: '600ms' }} data-testid="login-notice">
              <p className="text-xs font-semibold uppercase tracking-wider text-white/80 mb-0.5">Notice</p>
              <p className="text-sm text-zinc-400">{branding.notice}</p>
            </div>
          )}
        </div>
      </main>

      <footer className="relative z-10 px-6 md:px-12 py-4 text-center lg:text-left">
        <span className="text-xs text-white/40" data-testid="login-version">EQUIPR Horizon v{process.env.REACT_APP_VERSION || '0.0.0'}</span>
      </footer>
    </div>
  );
}
